/**
 * Deposits & Withdrawals Hooks
 * Manages deposit/withdrawal data for admin panel
 */

import { useApi, useMutation } from './useApi';
import { adminAPI } from '@/services/api';
import type {
  DepositsListResponse,
  DepositDetailResponse,
  ForceCompleteResponse,
  SyncDepositResponse,
  WithdrawalsListResponse,
  ResendWithdrawalResponse,
  AdminWithdrawalDetailResponseData,
} from '@/types';

// =============================================================================
// Deposits List Hook
// =============================================================================

export interface UseDepositsOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: DepositsListResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching and filtering deposits
 *
 * @example
 * const { deposits, stats, filterDeposits, refetch } = useDeposits({ fetchOnMount: true });
 * filterDeposits('pending', 50);
 */
export function useDeposits(options: UseDepositsOptions = {}) {
  const { fetchOnMount = false, onSuccess, onError } = options;

  const api = useApi<DepositsListResponse, [{ filter?: string; limit?: number; search?: string }?]>(
    (params) => adminAPI.getDeposits(params),
    { fetchOnMount, onSuccess, onError }
  );

  return {
    ...api,
    deposits: api.data?.deposits ?? [],
    stats: api.data?.stats ?? {},
    filter: api.data?.filter ?? 'all',
    filterDeposits: (filter?: string, limit?: number, search?: string) =>
      api.execute({ filter, limit, search }),
    searchDeposits: (search: string, limit?: number) =>
      api.execute({ search, limit }),
    refetch: () => api.execute(),
  };
}

// =============================================================================
// Deposit Detail Hook
// =============================================================================

export interface UseDepositDetailOptions {
  onSuccess?: (data: DepositDetailResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching single deposit details
 *
 * NOTE: Backend now returns AdminDepositDetail directly (Zod validated)
 * Fields include: referenceNumber, telegramId, username, firstName, amount,
 * bonusAmount, bonusPercentage, status, createdAt, completedAt, photoUrl,
 * forceCompleted, forceCompletedAt, forceCompletedBy, forceCompletedNote,
 * canForceComplete, ageMinutes, logs, etc.
 *
 * @example
 * const { deposit, canForceComplete, fetchDeposit } = useDepositDetail();
 * fetchDeposit('DEP-20260108-00001');
 */
export function useDepositDetail(options: UseDepositDetailOptions = {}) {
  const api = useApi<DepositDetailResponse, [string]>(
    (referenceNumber) => adminAPI.getDepositDetails(referenceNumber),
    options
  );

  return {
    ...api,
    // Backend now returns AdminDepositDetail directly (no wrapper)
    deposit: api.data ?? null,
    ageMinutes: api.data?.ageMinutes ?? 0,
    canForceComplete: api.data?.canForceComplete ?? false,
    logs: api.data?.logs ?? [],
    fetchDeposit: api.execute,
  };
}

// =============================================================================
// Deposit Actions Hook
// =============================================================================

export interface UseDepositActionsOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for deposit management actions
 *
 * @example
 * const { forceComplete, syncWithProvider, isLoading } = useDepositActions();
 * await forceComplete('txn-123', 'Manual credit');
 */
export function useDepositActions(options: UseDepositActionsOptions = {}) {
  const forceCompleteMutation = useMutation<ForceCompleteResponse, [string, string]>(
    (transactionId, adminNote) => adminAPI.forceCompleteDeposit(transactionId, { adminNote }),
    { onSuccess: () => options.onSuccess?.(), onError: options.onError }
  );

  const syncMutation = useMutation<SyncDepositResponse, [string]>(
    (transactionId) => adminAPI.syncWithProvider(transactionId),
    { onSuccess: () => options.onSuccess?.(), onError: options.onError }
  );

  return {
    forceComplete: forceCompleteMutation.mutate,
    isForceCompleting: forceCompleteMutation.isLoading,
    forceCompleteError: forceCompleteMutation.error,
    forceCompleteResult: forceCompleteMutation.data,

    syncWithProvider: syncMutation.mutate,
    isSyncing: syncMutation.isLoading,
    syncError: syncMutation.error,
    syncResult: syncMutation.data,

    isLoading: forceCompleteMutation.isLoading || syncMutation.isLoading,
  };
}

// =============================================================================
// Admin Withdrawal Detail Hook
// =============================================================================

export interface UseAdminWithdrawalDetailOptions {
  onSuccess?: (data: AdminWithdrawalDetailResponseData) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching single withdrawal details (Admin panel)
 *
 * NOTE: Different from useWithdrawalDetail in useWorker.ts (worker panel)
 *
 * @example
 * const { withdrawal, logs, fetchWithdrawal } = useAdminWithdrawalDetail();
 * fetchWithdrawal('WTH-20260107-00001');
 */
export function useAdminWithdrawalDetail(options: UseAdminWithdrawalDetailOptions = {}) {
  const api = useApi<AdminWithdrawalDetailResponseData, [string]>(
    (referenceNumber) => adminAPI.getWithdrawalDetails(referenceNumber),
    options
  );

  return {
    ...api,
    withdrawal: api.data ?? null,
    logs: api.data?.logs ?? [],
    fetchWithdrawal: api.execute,
  };
}

// =============================================================================
// Withdrawals List Hook (Admin view)
// =============================================================================

export interface UseWithdrawalsOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: WithdrawalsListResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching withdrawals (admin panel)
 *
 * Returns withdrawals + stats + available payment methods
 *
 * @example
 * const { withdrawals, stats, isLoading, searchWithdrawals } = useWithdrawals({ fetchOnMount: true });
 */
export function useWithdrawals(options: UseWithdrawalsOptions = {}) {
  const { fetchOnMount = false, onSuccess, onError } = options;

  const api = useApi<WithdrawalsListResponse, [{ limit?: number; search?: string }?]>(
    (params) => adminAPI.getWithdrawals(params),
    { fetchOnMount, onSuccess, onError }
  );

  return {
    ...api,
    withdrawals: api.data?.withdrawals ?? [],
    stats: api.data?.stats ?? { pending: { count: 0, total: 0 }, completed: { count: 0, total: 0 }, declined: { count: 0, total: 0 } },
    availablePaymentMethods: api.data?.availablePaymentMethods ?? [],
    searchWithdrawals: (search: string, limit?: number) =>
      api.execute({ search, limit }),
    refetch: () => api.execute(),
  };
}

// =============================================================================
// Withdrawal Actions Hook (Admin)
// =============================================================================

export interface UseWithdrawalActionsOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for admin withdrawal actions (resend)
 *
 * @example
 * const { resendWithdrawal, isResending } = useWithdrawalActions();
 * await resendWithdrawal('txn-123', 'Retry after fix');
 */
export function useWithdrawalActions(options: UseWithdrawalActionsOptions = {}) {
  const resendMutation = useMutation<ResendWithdrawalResponse, [string, string]>(
    (transactionId, adminNote) => adminAPI.resendWithdrawal(transactionId, { adminNote }),
    { onSuccess: () => options.onSuccess?.(), onError: options.onError }
  );

  return {
    resendWithdrawal: resendMutation.mutate,
    isResending: resendMutation.isLoading,
    resendError: resendMutation.error,
    resendResult: resendMutation.data,
  };
}
